import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import AdoptableShopCard from '@wix/da-shared-react/pkg/ShopCard/AdoptableShopCard';
import { MeasuredSlider as Slider } from '@wix/da-shared-react/pkg/Slider';
import { Url, URLS, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';

import DuperbrowseContext from '../../../../../contexts/DuperbrowseContext';
import WidgetBox, {
  Props as WidgetBoxProps,
} from '../../../../UserWidgets/_partials/WidgetBox';
import AddItemCard from '../../_partials/AddItemCard';
import AdoptablesEmptyState from '../../_partials/AdoptablesEmptyState';
import AdoptableSliderMobile from './AdoptableSliderMobile';

export interface Props {
  streamId: string;
  moduleId: number;
  deviations: PapiDeviation[];
  total: number;
  isOwner: boolean;
  username: string;
}

const AdoptableSlider: React.FC<Props & WidgetBoxProps> = ({
  isOwner,
  streamId,
  headerClassName,
  widgetMoveUp,
  widgetMoveDown,
  widgetRemove,
  moduleId,
  deviations,
  total,
  username,
  section,
  zone,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isEmpty = !deviations.length;
  const showAddItemCard = isOwner;

  if (isEmpty && !isOwner) {
    return null;
  }

  return (
    <WidgetBox
      section={section}
      zone={zone}
      header={t!('common.adoptables')}
      count={total}
      moduleId={moduleId}
      widgetMoveUp={widgetMoveUp}
      widgetMoveDown={widgetMoveDown}
      widgetRemove={widgetRemove}
      isEmpty={isEmpty}
      hasMore={!isEmpty} // until we implement inner slider overflow recognition
      emptyView={<AdoptablesEmptyState view={'wide'} />}
      seeAllUrl={Url.userShopPath(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.ADOPTABLES
      )}
      headerClassName={headerClassName}
    >
      <DuperbrowseContext
        streamId={streamId}
        deviations={deviations}
        parent={{ title: t('duperbrowse.backButton.profile.shop') }}
      >
        {isMobile ? (
          <AdoptableSliderMobile
            showAddItemCard={showAddItemCard}
            deviations={deviations}
          />
        ) : (
          <Slider
            renderSliderItems={elementSize => (
              <>
                {showAddItemCard && (
                  <AddItemCard
                    title={t('pages.shop.uploadMore.var.adoptables.title')}
                    subtitle={t(
                      'pages.shop.uploadMore.var.adoptables.subtitle'
                    )}
                    onClick={() => window.location.replace(URLS.SUBMIT)}
                    nonCoreMembershipView={
                      <AdoptablesEmptyState view={'small'} />
                    }
                    biData={BiData<DefaultBiEvent>({
                      evid: 2,
                      click_info: 'create_adoptable',
                      sectionname: 'adoptable',
                    })}
                    view={'small'}
                  />
                )}
                {deviations.map(deviation => (
                  <AdoptableShopCard
                    key={deviation.deviationId}
                    width={elementSize.width}
                    deviation={deviation}
                    withAuthorDetails={false}
                    withIndicators={false}
                  />
                ))}
              </>
            )}
          />
        )}
      </DuperbrowseContext>
    </WidgetBox>
  );
};

export default AdoptableSlider;
